import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { getLoginPath, getPricingPath, getSettingsPath } from "@/utils/localePaths";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { User, Settings, LogOut, Shield, Zap, CheckCircle } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Footer from "@/components/Footer";

function DistanceSalesContractPage() {
  const navigate = useNavigate();
  const { locale } = useTranslation();
  const { user, logout } = useAuth();

  const getPlanDisplayName = (planType) => {
    const planNames = {
      'free': 'Ücretsiz',
      'pro_monthly': 'Pro Aylık',
      'business_monthly': 'Business Aylık',
      'persona_staff': 'Çalışan'
    };
    return planNames[planType] || planType;
  };

  
  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-b from-black via-gray-900 to-black pt-2 pb-1 px-4">
        <div className="mb-1 px-4">
          <div className="flex justify-between items-center gap-4 bg-gray-900/50 backdrop-blur-sm rounded-lg p-2 border border-gray-800">
            <div 
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => navigate('/')}
                >
                  <img 
                    src="/opara-logo-trimmed.png" 
                    alt="OPARA Logo" 
                    className="h-10 w-auto object-contain"
                  />
                </div>
            {user && (
              <div className="flex items-center gap-2">
                {/* Analiz Hakkı */}
                <div className="text-sm bg-gray-800 px-3 py-1 rounded-lg border border-gray-700">
                  <span className="text-white">Analiz Hakkı: </span>
                  <span className="text-white font-bold">
                    {user?.plan_type === "persona_staff" ? "∞" : user?.credits_remaining}
                  </span>
                </div>
                
                {/* Paket Yükle Butonu */}
                <Link to={getPricingPath(locale)}>
                  <Button className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold py-1 text-sm">
                    Paket Yükle
                  </Button>
                </Link>
                
                {/* Avatar Dropdown Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFDD59] to-[#E6C84D] flex items-center justify-center hover:opacity-90 transition-opacity cursor-pointer border-2 border-gray-700 hover:border-[#FFDD59]">
                      <User className="w-4 h-4 text-black" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 bg-gray-900 border-gray-700" align="end">
                    <DropdownMenuLabel className="text-white">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">
                          {user?.first_name} {user?.last_name}
                          {user?.plan_type === "persona_staff" && (
                            <span className="text-xs text-[#FFDD59] ml-2">(Çalışan)</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400">
                          Plan: <span className="text-[#FFDD59]">{getPlanDisplayName(user?.plan_type)}</span>
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-gray-700" />
                    
                    <DropdownMenuItem asChild className="cursor-pointer text-gray-300 hover:text-white hover:bg-gray-800">
                      <Link to={getSettingsPath(locale)} className="flex items-center">
                        <Settings className="w-4 h-4 mr-2" />
                        Hesap Ayarları
                      </Link>
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator className="bg-gray-700" />
                    
                    <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-400 hover:text-red-300 hover:bg-gray-800">
                      <LogOut className="w-4 h-4 mr-2" />
                      Çıkış Yap
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* Sağ: Butonlar (giriş yapmamış kullanıcılar için) */}
            {!user && (
              <div className="flex items-center gap-2">
                <Link to={getPricingPath(locale)}>
                  <Button 
                    className="bg-[#FFDD59] hover:bg-[#FFE875] text-black font-bold w-40 py-1 text-sm"
                  >
                    Analiz Paketleri
                  </Button>
                </Link>
                
                <Link to={getLoginPath(locale)}>
                  <Button 
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold w-40 py-1 text-sm"
                  >
                    GİRİŞ YAP
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>
      
      {/* Geri Dön Butonu - Header altında, header ile aynı hizada */}
      <div className="px-4 mb-2">
        <div className="px-4">
          <Button 
            onClick={() => navigate(-1)}
            className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold"
          >
            ← Geri Dön
          </Button>
        </div>
      </div>

      <div className="relative z-10 py-4 px-4">
        <div className="max-w-5xl mx-auto">

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#FFDD59] mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              OPARA MESAFELİ SATIŞ SÖZLEŞMESİ
            </h1>
          </div>

          {/* Content */}
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-lg p-8 border border-gray-800 space-y-8">
            
            {/* Madde 1 */}
            <section>
              <h2 className="text-2xl font-semibold text-[#FFDD59] mb-4">Madde 1: Taraflar</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">1.1. SATICI (Veri Sorumlusu):</h3>
                  <div className="ml-4 text-gray-300 space-y-1">
                    <p><strong>Unvan:</strong> PERSONA ORGANİZASYON TİCARET LİMİTED ŞİRKETİ (Bundan sonra "Persona Ajans" veya "SATICI" olarak anılacaktır)</p>
                    <p><strong>MERSİS No:</strong> 728082174300001</p>
                    <p><strong>Adres:</strong> Zekeriyaköy Mah. Erguvanlı Cad. Ariköy Sitesi 28 No: 29 Sarıyer İstanbul</p>
                    <p><strong>E-posta:</strong> info@personaajans.com</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">1.2. ALICI (Kullanıcı):</h3>
                  <div className="ml-4 text-gray-300">
                    <p>opara.co (Site) üzerinden, işbu Sözleşme'de belirtilen dijital hizmetleri (Hizmet) satın almak için "Satın Al" veya "Ödeme Yap" butonuna basmadan önce, bu Sözleşme'yi okuyup kabul eden ve Site'ye kayıt olurken beyan ettiği Ad, Soyad, E-posta Adresi ve Fatura Bilgileri (TCKN veya VKN) bilgilerinin doğruluğunu taahhüt eden kayıtlı kullanıcıdır. (Bundan sonra "ALICI" veya "Kullanıcı" olarak anılacaktır)</p>
                    <p className="mt-2">ALICI ve SATICI, birlikte "Taraflar" olarak anılacaktır.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Madde 2 */}
            <section>
              <h2 className="text-2xl font-semibold text-[#FFDD59] mb-4">Madde 2: Sözleşmenin Konusu</h2>
              <div className="text-gray-300 space-y-3">
                <p>İşbu Sözleşme, 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği hükümleri uyarınca, ALICI'nın SATICI'ya ait opara.co internet sitesi üzerinden elektronik ortamda siparişini verdiği, nitelikleri ve satış fiyatı Sitede belirtilen dijital hizmetin (Analiz Paketi veya Abonelik) satışı ve teslimi ile ilgili Taraflar'ın hak ve yükümlülüklerini düzenler.</p>
                <p>ALICI, Site'ye kayıt olurken kabul ettiği "Kullanım Koşulları"nın (hizmetin niteliği, sorumluluk reddi vb. maddeleri içeren) işbu Sözleşme'nin ayrılmaz bir parçası olduğunu kabul eder.</p>
              </div>
            </section>

            {/* Madde 3 */}
            <section>
              <h2 className="text-2xl font-semibold text-[#FFDD59] mb-4">Madde 3: Sözleşme Konusu Hizmetin Tanımı ve Nitelikleri</h2>
              <div className="text-gray-300 space-y-4">
                <p>Hizmet, opara.co platformu üzerinde influencer maliyet-getiri analizi yapmak için kullanılan dijital "kredi" haklarının ALICI'nın hesabına tanımlanmasıdır. Hizmet, iki farklı modelde sunulur:</p>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">3.1. Tek Seferlik Paketler (Kullandıkça Öde):</h3>
                  <p>ALICI tarafından seçilen (örn: 10, 50 veya 100 kredi içeren) paketlerdir. Bu paketler, tek seferlik bir ödeme ile satın alınır ve otomatik olarak yenilenmez. Her paketin geçerlilik süresi (örn: 1 gün, 7 gün, 1 ay) satın alım anında açıkça belirtilir. Geçerlilik süresi sonunda kullanılmayan kredi hakları silinir, bir sonraki pakete devretmez.</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">3.2. Aylık Abonelik Planları (Pro ve Business):</h3>
                  <p>ALICI tarafından seçilen (örn: 250 veya 1000 kredi içeren) aylık yinelenen hizmet planlarıdır. Bu planlar, ALICI tarafından "Hesap Ayarları" bölümünden iptal edilene kadar her ay otomatik olarak yenilenir. Abonelik ücreti, her fatura döneminin başında ALICI'nın kayıtlı kredi kartından otomatik olarak tahsil edilir. Aylık kredi hakları, bir sonraki fatura dönemine devretmez; her fatura dönemi başında bakiye sıfırlanır ve o ayın kredi hakkı (örn: 250 kredi) hesaba yeniden yüklenir.</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">3.3. Abonelik Sahipleri için Ek Paket Kullanımı:</h3>
                  <p>Aylık Abonelik Planı (Pro veya Business) sahibi olan bir ALICI, aylık kredi limitini fatura dönemi bitmeden doldurursa, "Tek Seferlik Paketler" (Madde 3.1) satın alarak ek kredi hakkı edinebilir. Ancak, bu ek paketlerin geçerliliği, standart geçerlilik süresine (örn: 7 gün veya 1 ay) tabi olmayıp, ALICI'nın mevcut fatura döneminin sonuna kadar geçerlidir. Fatura dönemi yenilendiğinde, kullanılmayan ek paket kredileri de (ana paket gibi) silinir ve devretmez.</p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">3.4. "OPARA Yapsın" Hizmeti ve Teslimat Süresi:</h3>
                  <p>ALICI, satın aldığı kredileri kullanarak, veri toplama ve analiz işleminin SATICI tarafından yapılmasını talep ettiği "OPARA Yapsın" hizmetinden faydalanabilir. Bu hizmet kapsamında oluşturulan raporlar, ALICI'nın talebi sisteme girmesini takiben en geç 1 (bir) iş günü içinde tamamlanarak ALICI'ya teslim edilir. İşbu hizmetin bedeli, güncel tarife üzerinden ALICI'nın kredi bakiyesinden tahsil edilir.</p>
                </div>
              </div>
            </section>

            {/* Madde 4 */}
            <section>
              <h2 className="text-2xl font-semibold text-[#FFDD59] mb-4">Madde 4: Ödeme, Teslimat ve Faturalandırma</h2>
              <div className="text-gray-300 space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">4.1. Ödeme:</h3>
                  <p>Ödemeler, Iyzico (PayU) ödeme altyapısı aracılığıyla Kredi Kartı veya Banka Kartı ile peşin olarak alınır. ALICI, ödeme sayfasında girdiği kart bilgilerinin doğruluğundan sorumludur.</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">4.2. Hizmetin Teslimi:</h3>
                  <p>Sözleşme konusu Hizmet (Analiz Hakları), ödeme işleminin Iyzico tarafından SATICI'ya "Başarılı" olarak bildirilmesini takiben anında (derhal) ALICI'nın opara.co hesabına tanımlanarak teslim edilir.</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">4.3. Faturalandırma:</h3>
                  <p>ALICI, ödeme ekranında girdiği fatura bilgilerinin (TC Kimlik Numarası veya Firma Adı, Vergi Dairesi, Vergi Numarası) doğruluğunu kabul eder. SATICI, bu bilgilere dayanarak oluşturacağı e-Arşiv Fatura veya e-Fatura'yı, hizmet teslimini takiben yasal süreler içinde ALICI'nın kayıtlı e-posta adresine göndermekle yükümlüdür.</p>
                </div>
              </div>
            </section>

            {/* Madde 5 */}
            <section>
              <h2 className="text-2xl font-semibold text-[#FFDD59] mb-4">Madde 5: İptal, İade ve Cayma Hakkı</h2>
              <div className="text-gray-300 space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">5.1. Cayma Hakkının Kullanılamaması:</h3>
                  <p>ALICI, işbu Sözleşme ile satın aldığı hizmetin, Mesafeli Sözleşmeler Yönetmeliği'nin 15. Maddesinin 1. fıkrasının (ğ) bendi uyarınca "Elektronik ortamda anında ifa edilen hizmetler veya tüketiciye anında teslim edilen gayri maddi mallar" kapsamında olduğunu kabul eder. Ödeme işleminin başarıyla tamamlanması ve kredi haklarının ALICI'nın hesabına anında tanımlanması ile hizmetin ifası başladığından, ALICI'nın yasal olarak Cayma Hakkı bulunmamaktadır.</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">5.2. Para İadesi Politikası:</h3>
                  <p>Madde 5.1 uyarınca, ALICI tarafından satın alınan hiçbir "Tek Seferlik Paket" veya "Aylık Abonelik Planı" için (kullanılmış veya kullanılmamış olmasına bakılmaksızın) geriye dönük para iadesi (geri ödeme) yapılmaz.</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">5.3. Abonelik İptali:</h3>
                  <p>ALICI, "Pro Aylık" veya "Business Aylık" gibi otomatik yenilenen abonelik planlarını, bir sonraki yenileme tarihinden önce dilediği zaman opara.co sitesindeki "Hesap Ayarları" bölümünden iptal edebilir. İptal işlemi, o an içinde bulunulan fatura dönemi için para iadesi sağlamaz, ancak bir sonraki fatura döneminden itibaren aboneliğin yenilenmesini ve karttan tahsilat yapılmasını durdurur.</p>
                </div>
              </div>
            </section>

            {/* Madde 6 */}
            <section>
              <h2 className="text-2xl font-semibold text-[#FFDD59] mb-4">Madde 6: Fiyat Değişiklikleri</h2>
              <div className="text-gray-300 space-y-3">
                <p>SATICI (Persona Ajans), tüm paket ve planların fiyatlarını, kredi limitlerini ve geçerlilik sürelerini dilediği zaman değiştirme hakkını saklı tutar.</p>
                <p>Fiyat değişiklikleri, yeni satın alacak ALICI'lar için derhal geçerli olur.</p>
                <p>Hali hazırda "Aylık Abonelik" (Pro veya Business) sahibi olan mevcut ALICI'lar için ise, planlanan fiyat artışı, ALICI'nın bir sonraki fatura kesim tarihinden en az 30 (otuz) gün önce, ALICI'nın sisteme kayıtlı e-posta adresine bildirim yapılarak duyurulur. ALICI'nın, bu süre içinde "Hesap Ayarları" bölümünden aboneliğini iptal etmemesi halinde, abonelik bir sonraki fatura döneminde yeni fiyat üzerinden otomatik olarak yenilenir ve bu durum ALICI tarafından peşinen kabul edilmiş sayılır.</p>
              </div>
            </section>

            {/* Madde 7 */}
            <section>
              <h2 className="text-2xl font-semibold text-[#FFDD59] mb-4">Madde 7: Diğer Hükümler ve Yetkili Mahkeme</h2>
              <div className="text-gray-300 space-y-3">
                <p>İşbu Sözleşme'de hüküm bulunmayan hallerde (hizmetin niteliği, sorumluluk reddi, fikri mülkiyet vb. konularda), ALICI'nın Site'ye kayıt olurken kabul etmiş olduğu Kullanım Koşulları metni geçerlidir.</p>
                <p>İşbu Sözleşme'nin yorumlanmasında ve uygulanmasında Türkiye Cumhuriyeti yasaları geçerlidir.</p>
                <p>Bu Sözleşme'den kaynaklanabilecek her türlü uyuşmazlığın çözümünde İstanbul (Çağlayan) Mahkemeleri ve İcra Daireleri yetkilidir.</p>
                <p className="font-semibold text-white">Bu sözleşmeyi kabul ederek, yukarıdaki tüm maddeleri okuduğunuzu, anladığınızı ve kabul ettiğinizi beyan etmiş olursunuz.</p>
              </div>
            </section>

            {/* Last Updated */}
            <div className="pt-6 border-t border-gray-700">
              <p className="text-sm text-gray-400 text-center">
                Son Güncelleme Tarihi: 22 Kasım 2025
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default DistanceSalesContractPage;
